import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Blocks, RefreshCw, Trash2 } from "lucide-react";
import { useMarketplaces, usePendingDrafts, usePlugins } from "@/api/queries/plugins";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { SectionCard } from "@/components/SectionCard";
import { RemoveMarketplaceDialog } from "@/components/marketplace-actions/RemoveMarketplaceDialog";
import { AddMarketplaceDialog } from "@/components/marketplace-actions/AddMarketplaceDialog";
import { CreateMarketplaceDialog } from "@/components/scaffold-actions/CreateMarketplaceDialog";
import { marketplaceSourceLabel } from "@/components/marketplace-actions/sourceLabel";
import { CatalogSourceAlert, PluginStatusBadge } from "@/components/plugin-catalog/CatalogStatus";
import { PluginDecisionCells, PluginDecisionHeads } from "@/components/plugin-catalog/PluginDecisionCells";
import { BulkApproveBar } from "@/components/plugin-catalog/BulkApproveBar";
import { PluginCatalogMore, PluginCatalogToolbar, usePluginFilter } from "@/components/plugin-catalog/catalogFilter";
import { Link } from "react-router-dom";
import type { Marketplace } from "@/types";

// #232 (UC-4): Plugin-Katalog (admin). Oben die Marketplaces (add/remove), darunter der
// aggregierte Plugin-Katalog.
// #287: Der Katalog wird LIVE aus den `marketplace.json` gelesen — kein Sync-Knopf mehr
// (es gibt nichts nachzuziehen). „Aktualisieren" ist ein reiner Refetch.
export function PluginsList() {
  const markets = useMarketplaces();
  const plugins = usePlugins();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [scaffoldOpen, setScaffoldOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Marketplace | null>(null);

  const marketRows = markets.data ?? [];
  const pluginRows = plugins.data?.plugins ?? [];
  // #290 (UC-4): 287 Einträge sind ohne Suche/Filter/Deckel nicht bedienbar.
  const filter = usePluginFilter(pluginRows);
  // #291 (UC-3): die globale Freigabe-Arbeitsliste — hier zählen nur Katalog-Entwürfe.
  const pendingDrafts = usePendingDrafts();
  const offeneKataloge = (pendingDrafts.data ?? []).filter((p) => p.kind === "marketplace");
  const offeneEntwuerfe = offeneKataloge.reduce((n, p) => n + p.drafts.length, 0);

  return (
    <div className="space-y-5">
      {/* #291 (UC-3/US2): der Freigeber sieht SOFORT, ob etwas wartet — mit Sprung in
          die jeweilige Fach-Sicht (dort liegen Arbeitsliste + Freigabe). */}
      {/* Review-Fund 3: Ladefehler benennen — ein stilles Nicht-Banner läse sich als
          „nichts wartet". */}
      {pendingDrafts.isError && (
        <p className="text-[12.5px] text-danger" data-testid="pending-banner-error">
          Freigabe-Arbeitsliste konnte nicht geladen werden.
        </p>
      )}
      {offeneEntwuerfe > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn-border bg-warn-bg px-3 py-2 text-[12.5px]" data-testid="pending-banner">
          <span className="font-semibold">
            {offeneEntwuerfe === 1 ? "1 Entwurf wartet auf Freigabe" : `${offeneEntwuerfe} Entwürfe warten auf Freigabe`}
          </span>
          <span className="opacity-80">in:</span>
          {offeneKataloge.map((p) => (
            <Link
              key={p.repoId}
              to={p.marketplaceId ? `/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}` : "/repositories?repo=" + encodeURIComponent(p.repoId)}
              className="font-medium text-accent-text hover:underline"
            >
              {p.displayName} ({p.drafts.length})
            </Link>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">{marketRows.length} Marketplaces · {pluginRows.length} Plugins im Katalog</p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={plugins.isFetching}
            onClick={() => { void markets.refetch(); void plugins.refetch(); }}
            data-testid="action-refresh-catalog"
          >
            <RefreshCw className="h-4 w-4" />
            {plugins.isFetching ? "Lade …" : "Aktualisieren"}
          </Button>
          {/* #276 (UC-1): ZWEI verschiedene Dinge, deshalb zwei Knöpfe. „Einbinden"
              registriert einen Zeiger auf eine FREMDE, schon existierende Quelle;
              „Anlegen" erzeugt ein eigenes Repository, dessen Inhalt wir verwalten. */}
          <Button variant="outline" onClick={() => setCreateOpen(true)} data-testid="action-add-marketplace">
            <Plus className="h-4 w-4" />
            Marketplace einbinden
          </Button>
          <Button onClick={() => setScaffoldOpen(true)} data-testid="action-scaffold-marketplace">
            <Plus className="h-4 w-4" />
            Marketplace anlegen
          </Button>
        </div>
      </div>

      <ListTableShell
        isLoading={markets.isLoading && !markets.data}
        isError={markets.isError}
        errorMessage={(markets.error as Error | undefined)?.message}
        isEmpty={!markets.isLoading && marketRows.length === 0}
        emptyMessage="Noch keine Marketplaces"
        emptyHint="Füge einen Plugin-Marketplace hinzu (z.B. github org/repo). Die Plugins erscheinen danach im Katalog."
        onRetry={() => markets.refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Marketplace</TableHead>
              <TableHead>Quelle</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Plugins</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {marketRows.map((m) => {
              const source = plugins.data?.sources.find((s) => s.marketplaceId === m.id);
              const count = pluginRows.filter((p) => p.marketplaceId === m.id).length;
              return (
                <TableRow key={m.id} data-clickable="true" onClick={() => navigate(`/plugins/marketplaces/${m.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
                        <Blocks className="h-4 w-4" />
                      </span>
                      <div className="font-semibold text-text">{m.name}</div>
                    </div>
                  </TableCell>
                  <TableCell><span className="mono truncate text-[11.5px] text-text3">{marketplaceSourceLabel(m.source)}</span></TableCell>
                  <TableCell><Badge variant={m.scope === "platform" ? "enrolled" : "default"}>{m.scope}</Badge></TableCell>
                  <TableCell>
                    {source && !source.ok ? (
                      <Badge variant="busy" title={source.error ?? undefined}>nicht lesbar</Badge>
                    ) : (
                      <span className="text-[12.5px] text-text2">{count}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" title="Entfernen" onClick={(e) => { e.stopPropagation(); setRemoveTarget(m); }} data-testid={`marketplace-remove-${m.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListTableShell>

      {/* Aggregierter Plugin-Katalog über alle Marketplaces (live gelesen) */}
      <SectionCard icon={<Blocks className="h-[18px] w-[18px]" />} title="Plugin-Katalog" description="Live aus den marketplace.json der Quellen. Pro Agent freischaltbar.">
        <CatalogSourceAlert sources={plugins.data?.sources} />
        <BulkApproveBar plugins={filter.alleGefiltert} />
        {plugins.isLoading && !plugins.data ? (
          <p className="text-[13px] text-text3">Lade …</p>
        ) : plugins.isError ? (
          <p className="text-[13px] text-danger">Katalog konnte nicht geladen werden: {(plugins.error as Error | undefined)?.message}</p>
        ) : pluginRows.length === 0 ? (
          <p className="text-[13px] text-text3">Keine Plugins. Lege einen Marketplace an, dessen marketplace.json Plugins enthält.</p>
        ) : (
          <>
            <PluginCatalogToolbar filter={filter} />
            {filter.gesamt === 0 ? (
              <p className="text-[13px] text-text3">Kein Plugin passt zu Suche/Filter.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plugin</TableHead>
                    <TableHead>Marketplace</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <PluginDecisionHeads />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filter.sichtbar.map((p) => (
                    <TableRow key={p.ref} data-testid={`plugin-row-${p.marketplaceId}-${p.name}`}>
                      <TableCell>
                        {/* #290 (UC-3): Name führt ins Plugin-Detail */}
                        <Link
                          to={`/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}/${encodeURIComponent(p.name)}`}
                          className="font-medium text-accent-text hover:underline"
                        >
                          {p.name}
                        </Link>
                        {p.description && <div className="truncate text-[11.5px] text-text3" title={p.description}>{p.description}</div>}
                      </TableCell>
                      <TableCell><span className="text-[12.5px] text-text2">{p.marketplaceName}</span></TableCell>
                      <TableCell><span className="mono text-[12px] text-text3">{p.version ?? "—"}</span></TableCell>
                      <TableCell><PluginStatusBadge status={p.status} /></TableCell>
                      <PluginDecisionCells p={p} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <PluginCatalogMore filter={filter} />
          </>
        )}
      </SectionCard>

      {createOpen && <AddMarketplaceDialog open onOpenChange={setCreateOpen} />}
      {scaffoldOpen && (
        <CreateMarketplaceDialog
          open
          onOpenChange={setScaffoldOpen}
          onCreated={(id) => navigate(`/plugins/marketplaces/${id}`)}
        />
      )}
      {/* #290 (Review-Fund): geteilter Dialog — eine Fassung für Liste + Detail. */}
      {removeTarget && (
        <RemoveMarketplaceDialog
          marketplace={removeTarget}
          open
          onOpenChange={(o) => !o && setRemoveTarget(null)}
        />
      )}
    </div>
  );
}
